export type MarkdownInline =
  | { kind: 'text'; text: string }
  | { kind: 'strong' | 'emphasis' | 'code'; text: string }
  | { kind: 'link'; text: string; href: string };

export type MarkdownBlock =
  | { kind: 'heading'; level: 2 | 3; inlines: MarkdownInline[] }
  | { kind: 'paragraph' | 'quote'; inlines: MarkdownInline[] }
  | { kind: 'list'; ordered: boolean; items: MarkdownInline[][] };

const INLINE_TOKEN = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|\[[^\]\n]+\]\([^\s)]+\))/g;

const safeHref = (value: string): string | null => {
  try {
    const url = new URL(value, window.location.origin);
    return ['http:', 'https:', 'mailto:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

export function parseMarkdownInlines(value: string): MarkdownInline[] {
  const output: MarkdownInline[] = [];
  let offset = 0;
  for (const match of value.matchAll(INLINE_TOKEN)) {
    const index = match.index ?? 0;
    if (index > offset) output.push({ kind: 'text', text: value.slice(offset, index) });
    const token = match[0];
    if (token.startsWith('`')) {
      output.push({ kind: 'code', text: token.slice(1, -1) });
    } else if (token.startsWith('**')) {
      output.push({ kind: 'strong', text: token.slice(2, -2) });
    } else if (token.startsWith('*')) {
      output.push({ kind: 'emphasis', text: token.slice(1, -1) });
    } else {
      const parts = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (parts) output.push({ kind: 'link', text: parts[1], href: parts[2] });
      else output.push({ kind: 'text', text: token });
    }
    offset = index + token.length;
  }
  if (offset < value.length) output.push({ kind: 'text', text: value.slice(offset) });
  return output;
}

export function parseMarkdown(value: string): MarkdownBlock[] {
  const lines = value.replace(/\r\n?/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }
    const heading = /^(#{2,3})\s+(.+)$/.exec(line);
    if (heading) {
      blocks.push({
        kind: 'heading',
        level: heading[1].length as 2 | 3,
        inlines: parseMarkdownInlines(heading[2]),
      });
      index += 1;
      continue;
    }
    const list = /^(\s*)([-*]|\d+\.)\s+(.+)$/.exec(line);
    if (list) {
      const ordered = list[2].endsWith('.');
      const items: MarkdownInline[][] = [];
      while (index < lines.length) {
        const item = /^(\s*)([-*]|\d+\.)\s+(.+)$/.exec(lines[index]);
        if (!item || item[2].endsWith('.') !== ordered) break;
        items.push(parseMarkdownInlines(item[3]));
        index += 1;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }
    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      const parts: string[] = [];
      while (index < lines.length) {
        const next = /^>\s?(.*)$/.exec(lines[index]);
        if (!next) break;
        parts.push(next[1]);
        index += 1;
      }
      blocks.push({ kind: 'quote', inlines: parseMarkdownInlines(parts.join(' ')) });
      continue;
    }
    const paragraph: string[] = [];
    while (index < lines.length && lines[index].trim()) {
      if (
        paragraph.length &&
        (/^(#{2,3})\s+/.test(lines[index]) ||
          /^(\s*)([-*]|\d+\.)\s+/.test(lines[index]) ||
          /^>\s?/.test(lines[index]))
      ) {
        break;
      }
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ kind: 'paragraph', inlines: parseMarkdownInlines(paragraph.join(' ')) });
  }
  return blocks;
}

const renderInlines = (inlines: MarkdownInline[]): Node[] =>
  inlines.map((inline) => {
    if (inline.kind === 'text') return document.createTextNode(inline.text);
    if (inline.kind === 'link') {
      const href = safeHref(inline.href);
      if (!href) return document.createTextNode(inline.text);
      const link = document.createElement('a');
      link.href = href;
      link.textContent = inline.text;
      if (link.origin !== window.location.origin) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer nofollow';
      }
      return link;
    }
    const element = document.createElement(
      inline.kind === 'strong' ? 'strong' : inline.kind === 'emphasis' ? 'em' : 'code',
    );
    element.textContent = inline.text;
    return element;
  });

export function renderMarkdown(value: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  for (const block of parseMarkdown(value)) {
    if (block.kind === 'list') {
      const list = document.createElement(block.ordered ? 'ol' : 'ul');
      for (const item of block.items) {
        const row = document.createElement('li');
        row.append(...renderInlines(item));
        list.appendChild(row);
      }
      fragment.appendChild(list);
      continue;
    }
    const element = document.createElement(
      block.kind === 'heading' ? `h${block.level}` : block.kind === 'quote' ? 'blockquote' : 'p',
    );
    element.append(...renderInlines(block.inlines));
    fragment.appendChild(element);
  }
  return fragment;
}
